import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface BulkChargeStudentsParams {
  studentId: Array<string>;
  amount: string;
  chargeName: string;
  isSingleStudent?: boolean;
  transactionCodeId?: string;
  schedulePayment?: boolean;
  scheduledDate?: string | null;
  skipPaymentMethod?: boolean;
  /** Saved payment method for single-student manual charge (V2). */
  paymentMethodId?: string;
  allowDuplicateCharge?: boolean;
  /** When true, call V2 bulk-charge orchestrator. */
  isV2?: boolean;
}

export type BulkChargeStudentStatus =
  | 'charged'
  | 'scheduled'
  | 'skipped'
  | 'failed';

export interface BulkChargeStudentResult {
  studentId: string;
  status: BulkChargeStudentStatus;
  reason?: string;
  invoiceId?: string;
}

export type BulkChargeBatchOutcome = 'success' | 'partial' | 'failed';

export interface BulkChargeStudentsResponse {
  success: boolean;
  /** Present on V2 responses; mirrors enrollment/hourly batch outcome. */
  outcome?: BulkChargeBatchOutcome;
  message: string;
  bulkPaymentId?: string;
  summary?: {
    total: number;
    charged: number;
    scheduled: number;
    skipped: number;
    failed: number;
  };
  results?: Array<BulkChargeStudentResult>;
}

export type BulkChargeOutcome =
  | { type: 'success'; message: string }
  | { type: 'partial'; message: string }
  | { type: 'error'; message: string };

/**
 * Single-student manual charge.
 * Reads that student's result only — no results[0] fallback.
 */
export const getSingleStudentChargeOutcome = (
  data: BulkChargeStudentsResponse,
  studentId: string,
): BulkChargeOutcome => {
  const result = data.results?.find((entry) => entry.studentId === studentId);

  if (
    result &&
    (result.status === 'charged' || result.status === 'scheduled')
  ) {
    return {
      type: 'success',
      message:
        result.status === 'scheduled'
          ? 'Charge scheduled successfully.'
          : 'Charge submitted successfully.',
    };
  }

  return {
    type: 'error',
    message:
      result?.reason ||
      data.message ||
      'Charge could not be processed. Please try again.',
  };
};

/**
 * Multi-student bulk charge.
 * Prefer V2 `outcome` when present; otherwise use success/summary (v1).
 * Exact per-student reasons stay on backend results / invoices / txns.
 */
export const getBulkChargeOutcome = (
  data: BulkChargeStudentsResponse,
): BulkChargeOutcome => {
  if (data.outcome === 'partial') {
    return {
      type: 'partial',
      message: data.message || 'Some charges could not be processed.',
    };
  }

  if (data.outcome === 'failed') {
    return {
      type: 'error',
      message:
        data.message || 'Charge could not be processed. Please try again.',
    };
  }

  if (data.outcome === 'success' || data.success) {
    return {
      type: 'success',
      message: data.message || 'Charge successful!',
    };
  }

  const { charged = 0, scheduled = 0 } = data.summary ?? {};
  if (charged > 0 || scheduled > 0) {
    return {
      type: 'partial',
      message: data.message || 'Some charges could not be processed.',
    };
  }

  return {
    type: 'error',
    message: data.message || 'Charge could not be processed. Please try again.',
  };
};

export const bulkChargeStudentsAPI = async ({
  isV2,
  ...body
}: BulkChargeStudentsParams): Promise<BulkChargeStudentsResponse> => {
  const path = isV2
    ? '/v2/bulk-charges'
    : '/payment-processor/bulk-charge-students';
  const response = await baseAPI.post<BulkChargeStudentsResponse>(path, body);
  return response.data;
};

export const useBulkChargeStudents = () => {
  return useMutation({
    mutationFn: (body: BulkChargeStudentsParams) => bulkChargeStudentsAPI(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === KEYS.STUDENT_PAYMENT_HISTORY,
      });
    },
  });
};
